import { COLORES_PLACA } from '@/lib/guiaElementosMusicales';
import { NOTA_A_COLOR_PLACA, NOTA_A_SOLFEO } from '@/lib/melodiaColoresPlacas';
import type { TimeSignature } from '../rhythm/ecoRitmoConfig';
import type { MelodyKeyId, Subdivision } from './melodiaKeys';
import { pitchesForKey } from './melodiaKeys';

export type MelodyPitchId =
  | 'Do4'
  | 'Re4'
  | 'Mi4'
  | 'Fa4'
  | 'Sol4'
  | 'La4'
  | 'Si4'
  | 'Do5';

export type MelodySoundId = 'piano' | 'metalofono' | 'flauta';

/** Resolución interna fija: 4 subdivisiones por tiempo (semicorcheas). */
export const INTERNAL_SUBDIVISION = 4;

/** [measureIndex][internalSlot] → pitch or null */
export type MelodyGrid = (MelodyPitchId | null)[][];

export interface GridNoteRef {
  measureIndex: number;
  internalSlot: number;
}

export interface MelodiaSessionConfig {
  measureCount: number;
  timeSignature: TimeSignature;
  bpm: number;
  soundId: MelodySoundId;
  keyId: MelodyKeyId;
  /** Solo afecta la visualización; los datos internos no cambian. */
  subdivision: Subdivision;
  grid: MelodyGrid;
}

export interface MelodyNoteBlock {
  pitchId: MelodyPitchId;
  label: string;
  letter: string;
  color: string;
  fill: string;
  staffLine: number;
}

export interface MelodyPlaybackEvent {
  step: number;
  measureIndex: number;
  internalSlot: number;
  pitchId: MelodyPitchId;
  timeMs: number;
}

export const MAX_MEASURES = 5;
export const MIN_BPM = 50;
export const MAX_BPM = 120;

const PITCH_LETTER: Record<MelodyPitchId, string> = {
  Do4: 'C',
  Re4: 'D',
  Mi4: 'E',
  Fa4: 'F',
  Sol4: 'G',
  La4: 'A',
  Si4: 'B',
  Do5: 'C',
};

export const ALL_GRID_PITCHES: MelodyPitchId[] = [
  'Do5',
  'Si4',
  'La4',
  'Sol4',
  'Fa4',
  'Mi4',
  'Re4',
  'Do4',
];

const STAFF_LINE: Record<MelodyPitchId, number> = {
  Do4: 0,
  Re4: 1,
  Mi4: 2,
  Fa4: 2.5,
  Sol4: 3,
  La4: 4,
  Si4: 5,
  Do5: 6,
};

function pitchColor(pitchId: MelodyPitchId): { color: string; fill: string } {
  const letter = PITCH_LETTER[pitchId];
  const plateKey = NOTA_A_COLOR_PLACA[letter as keyof typeof NOTA_A_COLOR_PLACA] ?? 'azul';
  const plate = COLORES_PLACA[plateKey as keyof typeof COLORES_PLACA];
  return { color: plate?.stroke ?? '#334155', fill: plate?.fill ?? '#1E88E5' };
}

export function noteBlockForPitch(pitchId: MelodyPitchId): MelodyNoteBlock {
  const letter = PITCH_LETTER[pitchId];
  const solfeo = NOTA_A_SOLFEO[letter as keyof typeof NOTA_A_SOLFEO] ?? pitchId.slice(0, 2);
  const octave = pitchId.endsWith('5') ? '5' : '4';
  const { color, fill } = pitchColor(pitchId);
  return {
    pitchId,
    label: `${solfeo}${octave}`,
    letter,
    color,
    fill,
    staffLine: STAFF_LINE[pitchId],
  };
}

export function gridPitchesForKey(keyId: MelodyKeyId): MelodyPitchId[] {
  const allowed = new Set(pitchesForKey(keyId));
  return ALL_GRID_PITCHES.filter((p) => allowed.has(p));
}

export function beatsPerMeasure(ts: TimeSignature): number {
  const [n] = ts.split('/').map(Number);
  return n || 4;
}

export function internalSlotsPerMeasure(ts: TimeSignature): number {
  return beatsPerMeasure(ts) * INTERNAL_SUBDIVISION;
}

export function visualSlotsPerMeasure(ts: TimeSignature, subdivision: Subdivision): number {
  return beatsPerMeasure(ts) * subdivision;
}

export function noteRefKey(ref: GridNoteRef): string {
  return `${ref.measureIndex}:${ref.internalSlot}`;
}

export function visualSlotRange(
  visualSlot: number,
  subdivision: Subdivision
): { start: number; end: number } {
  const factor = INTERNAL_SUBDIVISION / subdivision;
  const start = visualSlot * factor;
  return { start, end: start + factor };
}

export function internalToVisualSlot(internalSlot: number, subdivision: Subdivision): number {
  const factor = INTERNAL_SUBDIVISION / subdivision;
  return Math.floor(internalSlot / factor);
}

export function clampMeasureCount(count: number): number {
  return Math.min(MAX_MEASURES, Math.max(1, Math.round(count)));
}

export function emptyGrid(measureCount: number, ts: TimeSignature): MelodyGrid {
  const slots = internalSlotsPerMeasure(ts);
  return Array.from({ length: measureCount }, () => Array.from({ length: slots }, () => null));
}

export function createDefaultSessionConfig(): MelodiaSessionConfig {
  const timeSignature: TimeSignature = '4/4';
  return {
    measureCount: 2,
    timeSignature,
    bpm: 80,
    soundId: 'piano',
    keyId: 'C-major',
    subdivision: 4,
    grid: emptyGrid(2, timeSignature),
  };
}

export function noteInVisualCell(
  grid: MelodyGrid,
  measureIndex: number,
  visualSlot: number,
  subdivision: Subdivision
): MelodyPitchId | null {
  const { start, end } = visualSlotRange(visualSlot, subdivision);
  for (let i = start; i < end; i++) {
    const pitch = grid[measureIndex]?.[i] ?? null;
    if (pitch) return pitch;
  }
  return null;
}

export function clearInternalRange(
  grid: MelodyGrid,
  measureIndex: number,
  start: number,
  end: number
): MelodyGrid {
  return grid.map((measure, mi) =>
    mi === measureIndex
      ? measure.map((cell, si) => (si >= start && si < end ? null : cell))
      : measure
  );
}

export function setNoteAtVisualCell(
  grid: MelodyGrid,
  measureIndex: number,
  visualSlot: number,
  subdivision: Subdivision,
  pitch: MelodyPitchId | null
): MelodyGrid {
  const { start, end } = visualSlotRange(visualSlot, subdivision);
  let next = clearInternalRange(grid, measureIndex, start, end);
  if (pitch) {
    next = next.map((measure, mi) =>
      mi === measureIndex ? measure.map((cell, si) => (si === start ? pitch : cell)) : measure
    );
  }
  return next;
}

export function toggleVisualCell(
  grid: MelodyGrid,
  measureIndex: number,
  visualSlot: number,
  subdivision: Subdivision,
  pitch: MelodyPitchId
): MelodyGrid {
  const existing = noteInVisualCell(grid, measureIndex, visualSlot, subdivision);
  if (existing === pitch) {
    const { start, end } = visualSlotRange(visualSlot, subdivision);
    return clearInternalRange(grid, measureIndex, start, end);
  }
  return setNoteAtVisualCell(grid, measureIndex, visualSlot, subdivision, pitch);
}

export function resizeGridForTimeSignature(grid: MelodyGrid, ts: TimeSignature): MelodyGrid {
  const slots = internalSlotsPerMeasure(ts);
  return grid.map((measure) => {
    const next = measure.slice(0, slots);
    while (next.length < slots) next.push(null);
    return next;
  });
}

export function filterGridForKey(grid: MelodyGrid, keyId: MelodyKeyId): MelodyGrid {
  const allowed = new Set(pitchesForKey(keyId));
  return grid.map((measure) => measure.map((cell) => (cell && allowed.has(cell) ? cell : null)));
}

export function clearNoteRefs(grid: MelodyGrid, refs: GridNoteRef[]): MelodyGrid {
  const keys = new Set(refs.map(noteRefKey));
  return grid.map((measure, mi) =>
    measure.map((cell, si) => (keys.has(`${mi}:${si}`) ? null : cell))
  );
}

export function collectNotesInLasso(
  grid: MelodyGrid,
  pitches: MelodyPitchId[],
  subdivision: Subdivision,
  anchor: { measureIndex: number; visualSlot: number; pitchId: MelodyPitchId },
  target: { measureIndex: number; visualSlot: number; pitchId: MelodyPitchId }
): GridNoteRef[] {
  const miMin = Math.min(anchor.measureIndex, target.measureIndex);
  const miMax = Math.max(anchor.measureIndex, target.measureIndex);
  const vsMin = Math.min(anchor.visualSlot, target.visualSlot);
  const vsMax = Math.max(anchor.visualSlot, target.visualSlot);
  const pMin = Math.min(pitches.indexOf(anchor.pitchId), pitches.indexOf(target.pitchId));
  const pMax = Math.max(pitches.indexOf(anchor.pitchId), pitches.indexOf(target.pitchId));

  const refs: GridNoteRef[] = [];
  for (let mi = miMin; mi <= miMax; mi++) {
    for (let vs = vsMin; vs <= vsMax; vs++) {
      const { start, end } = visualSlotRange(vs, subdivision);
      for (let si = start; si < end; si++) {
        const pitch = grid[mi]?.[si] ?? null;
        if (!pitch) continue;
        const pi = pitches.indexOf(pitch);
        if (pi >= pMin && pi <= pMax) {
          refs.push({ measureIndex: mi, internalSlot: si });
        }
      }
    }
  }
  return refs;
}

export function isGridNonEmpty(grid: MelodyGrid): boolean {
  return grid.some((measure) => measure.some((cell) => cell != null));
}

export function totalInternalSteps(config: MelodiaSessionConfig): number {
  return config.measureCount * internalSlotsPerMeasure(config.timeSignature);
}

export function internalStepDurationMs(config: MelodiaSessionConfig): number {
  return 60000 / config.bpm / INTERNAL_SUBDIVISION;
}

export function buildPlaybackEvents(config: MelodiaSessionConfig): MelodyPlaybackEvent[] {
  const slots = internalSlotsPerMeasure(config.timeSignature);
  const stepMs = internalStepDurationMs(config);
  const events: MelodyPlaybackEvent[] = [];

  for (let m = 0; m < config.measureCount; m++) {
    for (let si = 0; si < slots; si++) {
      const pitchId = config.grid[m]?.[si] ?? null;
      if (!pitchId) continue;
      const step = m * slots + si;
      events.push({
        step,
        measureIndex: m,
        internalSlot: si,
        pitchId,
        timeMs: step * stepMs,
      });
    }
  }

  return events;
}

export function playbackDurationMs(config: MelodiaSessionConfig): number {
  return totalInternalSteps(config) * internalStepDurationMs(config) + 400;
}

export function visualSlotToBeatSub(
  visualSlot: number,
  subdivision: Subdivision
): { beatIndex: number; subIndex: number } {
  return {
    beatIndex: Math.floor(visualSlot / subdivision),
    subIndex: visualSlot % subdivision,
  };
}

export function playheadOnVisualCell(
  activeInternalStep: number,
  measureIndex: number,
  visualSlot: number,
  config: MelodiaSessionConfig
): boolean {
  if (activeInternalStep < 0) return false;
  const slots = internalSlotsPerMeasure(config.timeSignature);
  const globalStart = measureIndex * slots;
  const { start, end } = visualSlotRange(visualSlot, config.subdivision);
  const cellStart = globalStart + start;
  const cellEnd = globalStart + end;
  return activeInternalStep >= cellStart && activeInternalStep < cellEnd;
}
